import { z } from 'zod';

/**
 * Soft edit-lease primitives for the flow editor. Backed by the
 * `flow_edit_leases` DB table (one row per flow, lazy-expired on
 * read). The lease is a UX layer — it gives the second editor a
 * clear "Sue is editing — take over?" banner instead of silent
 * last-write-wins. The correctness layer is the `draft_version`
 * optimistic check on every PUT.
 *
 * Lifecycle:
 *
 *   1. Editor mounts -> POST /api/flows/:id/lease.
 *   2. Server upserts; returns either { ok: true, lease } or
 *      { ok: false, conflict: lease }.
 *   3. On `ok: true`, the editor heartbeats every 60s by re-POSTing
 *      with the same `holderSessionId`. Server bumps
 *      `last_heartbeat_at` + `expires_at`.
 *   4. Editor unmount / `visibilitychange:hidden` / `beforeunload`
 *      -> DELETE /api/flows/:id/lease (best-effort, fire-and-forget).
 *   5. Stale leases (expires_at < NOW()) are auto-evicted on the
 *      next acquire; no sweep cron needed.
 *
 * Pre-PMF scale: lease check is one PK lookup per editor mount + one
 * UPSERT per 60s. Move to Redis + presence channel when this becomes
 * material.
 */

export const FlowLeaseSchema = z.object({
  flowId: z.string().uuid(),
  /**
   * Branch the lease is for. Nullable for backwards-compat with V1
   * leases that predate migration 047 (those rows had `branch_id IS
   * NULL` and implicitly meant the default branch). New leases
   * always carry a branchId.
   */
  branchId: z.string().uuid().nullable().optional(),
  holderId: z.string().uuid(),
  holderName: z.string(),
  /** Tab/window-scoped id so two tabs from the same user don't fight. */
  holderSessionId: z.string(),
  acquiredAt: z.string(),
  lastHeartbeatAt: z.string(),
  expiresAt: z.string(),
});
export type FlowLease = z.infer<typeof FlowLeaseSchema>;

export const AcquireLeaseRequestSchema = z.object({
  /**
   * Stable per browser-tab identifier (e.g. crypto.randomUUID() once
   * per editor mount, persisted in sessionStorage). Lets the server
   * tell heartbeats apart from a fresh acquire by the same user.
   */
  holderSessionId: z.string().min(1).max(128),
});
export type AcquireLeaseRequest = z.infer<typeof AcquireLeaseRequestSchema>;

/**
 * Two-shape response. `ok: true` means the requester now holds the
 * lease (either freshly acquired or a refresh of their own). `ok:
 * false` carries the current holder's summary so the editor can
 * render the banner without a follow-up GET.
 */
export const AcquireLeaseResponseSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    lease: FlowLeaseSchema,
  }),
  z.object({
    ok: z.literal(false),
    /** Currently active lease blocking the acquire. */
    conflict: FlowLeaseSchema,
  }),
]);
export type AcquireLeaseResponse = z.infer<typeof AcquireLeaseResponseSchema>;

/**
 * Stealing the lease forces the previous holder's next save to 409
 * with FLOW_DRAFT_VERSION_STALE (because takeovers always bump a
 * sentinel version on the row in the same transaction). Audit log
 * gets a FLOW_LEASE_TAKEOVER row.
 */
export const TakeoverLeaseRequestSchema = AcquireLeaseRequestSchema;
export type TakeoverLeaseRequest = z.infer<typeof TakeoverLeaseRequestSchema>;

/** Always returns the requester's now-active lease. */
export const TakeoverLeaseResponseSchema = z.object({
  lease: FlowLeaseSchema,
});
export type TakeoverLeaseResponse = z.infer<typeof TakeoverLeaseResponseSchema>;
